import { createContext, useContext, useState, useEffect, useCallback, useReducer, useRef, type ReactNode } from 'react';
import { apiClient, getErrorCode } from '@/lib/api-client';
import { setTimeFormatPreference } from '@/lib/dates';
import { isSignInRequest } from '@/lib/auth-urls';
import { normalizeRole, type ApiResponse, type AuthUser, type LoginRequest, type LoginResponse, type RegisterRequest, type TwoStepChallenge, type UserRole } from '@bakedshift/shared';

// SECURITY: Tokens are now stored in HttpOnly cookies (XSS-safe)
// Only non-sensitive user data is stored in localStorage for UI purposes
const USER_KEY = 'bakedshift_auth_user';

// Token refresh timing (refresh 1 minute before expiry)
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
const ACCESS_TOKEN_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes (must match backend)

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Signs in — or, for an account with two-step sign-in, answers the challenge for the code step. */
  login: (credentials: LoginRequest) => Promise<LoginOutcome>;
  /** The code step (uiv2 BE7): the challenge from `login`, and a code from the app or a recovery code. */
  completeTwoStep: (challenge: string, code: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<{ requiresOnboarding: boolean; selectedRole: UserRole }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export type LoginOutcome = { kind: 'signed-in' } | { kind: 'two-step'; challenge: string };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/** A cached or server user with every membership role in the current names. */
export function toCurrentRoles(u: AuthUser): AuthUser {
  return {
    ...u,
    isPlatformAdmin: u.isPlatformAdmin === true,
    // A user cached before uiv2 D3 has no flag; absent is not flagged.
    requirePasswordChange: u.requirePasswordChange === true,
    workspaces: (u.workspaces ?? []).flatMap((w) => {
      const role = normalizeRole(w.role);
      return role === null ? [] : [{ ...w, role }];
    }),
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Every user that enters state is read through `toCurrentRoles`: the one in
  // localStorage, or from a server still on the old enum, can carry `admin` or
  // `superuser` (renamed 2026-09-25). A reducer, so the setter stays a stable dispatch.
  const [user, setUser] = useReducer(
    (_: AuthUser | null, next: AuthUser | null) => (next ? toCurrentRoles(next) : null),
    null,
  );
  // The reader's clock for every formatted time (uiv2 BE5). Set during render,
  // before the children render, so a changed preference shows on the render
  // that carries it. A user cached before BE5 has none: the language decides.
  setTimeFormatPreference(user?.timeFormat ?? 'auto');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [_refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Ref to track refresh timer
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The in-flight /auth/me re-read after a PASSWORD_CHANGE_REQUIRED refusal.
  const rereadMeRef = useRef<Promise<void> | null>(null);
  // Ref to track if we're currently refreshing (prevent multiple simultaneous refreshes)
  const isRefreshingRef = useRef(false);

  /**
   * Clear all auth state and storage
   * SECURITY: Tokens are in HttpOnly cookies - server clears them on logout
   * We only clear local state and non-sensitive user data from localStorage
   */
  const clearAuth = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    // Only remove user data - tokens are in HttpOnly cookies (cleared by server)
    localStorage.removeItem(USER_KEY);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  /**
   * Save auth state after login/register
   * SECURITY: Tokens are set as HttpOnly cookies by the backend
   * We only store user data locally for UI purposes (not sensitive)
   */
  const saveAuth = useCallback((newUser: AuthUser) => {
    // SECURITY: Tokens are ONLY in HttpOnly cookies — never exposed to JS.
    // We mark as 'cookie-managed' for the isAuthenticated check.
    setAccessToken('cookie-managed');
    setRefreshToken('cookie-managed');
    setUser(newUser);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
  }, []);

  /**
   * Refresh the access token using the refresh token cookie
   * SECURITY: Refresh token is in HttpOnly cookie - sent automatically
   */
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      return false;
    }

    isRefreshingRef.current = true;

    try {
      // Refresh token is sent via HttpOnly cookie automatically (withCredentials: true)
      const response = await apiClient.post('/auth/refresh', {});

      if (response.data.success) {
        const { user: newUser } = response.data.data;
        saveAuth(newUser);
        return true;
      }
      return false;
    } catch {
      // Refresh failed - clear auth and redirect to login
      clearAuth();
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [saveAuth, clearAuth]);

  /**
   * Schedule token refresh before it expires
   */
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Schedule refresh 1 minute before token expires
    const refreshTime = ACCESS_TOKEN_EXPIRY_MS - TOKEN_REFRESH_BUFFER_MS;

    refreshTimerRef.current = setTimeout(async () => {
      const success = await refreshAccessToken();
      if (success) {
        // Schedule next refresh
        scheduleTokenRefresh();
      }
    }, refreshTime);
  }, [refreshAccessToken]);

  /**
   * Initialize auth state from session
   * SECURITY: Session is validated via HttpOnly cookies - not localStorage tokens
   */
  useEffect(() => {
    const initAuth = async () => {
      const savedUser = localStorage.getItem(USER_KEY);

      // If we have cached user data, try to verify the session is still valid
      if (savedUser) {
        try {
          // Verify session via /auth/me - cookies sent automatically
          const response = await apiClient.get('/auth/me');

          if (response.data.success) {
            const userData = response.data.data;
            setUser(userData);
            // Update localStorage with fresh user data
            localStorage.setItem(USER_KEY, JSON.stringify(userData));
            // Mark as authenticated (tokens are in cookies)
            setAccessToken('cookie-managed');
            setRefreshToken('cookie-managed');
            scheduleTokenRefresh();
          } else {
            // Session invalid, clear local state
            clearAuth();
          }
        } catch {
          // Session verification failed - might be expired, try refresh
          const refreshSuccess = await refreshAccessToken();
          if (refreshSuccess) {
            scheduleTokenRefresh();
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [scheduleTokenRefresh, refreshAccessToken, clearAuth]);

  /**
   * Set up axios interceptors for token refresh on 401
   * SECURITY: No Authorization header needed - cookies handle auth
   */
  useEffect(() => {
    // Response interceptor to handle 401 errors and refresh token
    const responseInterceptorId = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // A refused SIGN-IN is not an expired session (uiv2 BE7): a wrong
        // password or code must reach the Login page as an error. Refreshing
        // here failed (there is no session yet) and then navigated to /login —
        // a full reload of the page the user was on, so "incorrect password"
        // never showed, and the two-step challenge would be lost with it.
        if (error.response?.status === 401 && isSignInRequest(originalRequest.url)) {
          return Promise.reject(error);
        }

        // If error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Don't try to refresh if this was a refresh request. Request URLs
          // are relative to the client's /api base — this compared against
          // '/api/auth/refresh' and never matched.
          if (originalRequest.url?.includes('/auth/refresh')) {
            clearAuth();
            window.location.href = '/login';
            return Promise.reject(error);
          }

          // Try to refresh the token (via HttpOnly cookie)
          const refreshSuccess = await refreshAccessToken();

          if (refreshSuccess) {
            // Retry the original request - new cookie is set automatically
            return apiClient(originalRequest);
          }

          // Refresh failed, redirect to login
          window.location.href = '/login';
        }

        // uiv2 D3: an admin reset this password mid-session. Re-read /auth/me,
        // which now carries the flag; ProtectedRoute then shows only the
        // change screen. Not a sign-out — the session is still valid.
        // A page's parallel queries are refused together; they share one read.
        if (error.response?.status === 403 && getErrorCode(error) === 'PASSWORD_CHANGE_REQUIRED') {
          rereadMeRef.current ??= apiClient.get('/auth/me')
            .then((me) => {
              if (me.data?.success && me.data.data) {
                setUser(me.data.data);
                localStorage.setItem(USER_KEY, JSON.stringify(me.data.data));
              }
            })
            .catch(() => undefined) // the original refusal still propagates below
            .finally(() => { rereadMeRef.current = null; });
          await rereadMeRef.current;
        }

        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(responseInterceptorId);
    };
  }, [refreshAccessToken, clearAuth]);

  /**
   * Login with username and password
   */
  const login = useCallback(async (credentials: LoginRequest): Promise<LoginOutcome> => {
    const response = await apiClient.post<ApiResponse<LoginResponse | TwoStepChallenge>>('/auth/login', credentials);
    const data = response.data.data;
    // The password was right, but the account has two-step sign-in: no session yet.
    if (data && 'twoFactorRequired' in data) return { kind: 'two-step', challenge: data.challenge };
    if (!data) throw new Error('empty sign-in response');

    saveAuth(data.user);
    scheduleTokenRefresh();
    return { kind: 'signed-in' };
  }, [saveAuth, scheduleTokenRefresh]);

  const completeTwoStep = useCallback(async (challenge: string, code: string) => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login/2fa', { challenge, code });
    const data = response.data.data;
    if (!data) throw new Error('empty sign-in response');
    saveAuth(data.user);
    scheduleTokenRefresh();
  }, [saveAuth, scheduleTokenRefresh]);

  /**
   * Register a new account
   */
  const register = useCallback(async (data: RegisterRequest) => {
    const response = await apiClient.post('/auth/register', data);
    const { user: newUser, requiresOnboarding, selectedRole } = response.data.data;

    saveAuth(newUser);
    scheduleTokenRefresh();

    return { requiresOnboarding, selectedRole };
  }, [saveAuth, scheduleTokenRefresh]);

  /**
   * Logout and revoke tokens
   * SECURITY: Server clears HttpOnly cookies on logout
   */
  const logout = useCallback(async () => {
    try {
      // Tokens are in cookies - server will read and revoke them
      await apiClient.post('/auth/logout', {});
    } catch {
      // Ignore logout errors - still clear local state
    }

    clearAuth();
  }, [clearAuth]);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    if (!user) return;

    try {
      // Session is validated via cookies automatically
      const response = await apiClient.get('/auth/me');
      const updatedUser = response.data.data;

      setUser(updatedUser);
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
    } catch {
      // If refresh fails, logout
      await logout();
    }
  }, [user, logout]);

  const value: AuthContextType = {
    user,
    token: accessToken,
    isAuthenticated: !!accessToken && !!user,
    isLoading,
    login,
    completeTwoStep,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
